import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ServicesSection from "../components/ServicesSection";
import WatchOfTheWeek from "../components/WatchOfTheWeek";
import logoEmblem from "../assets/logo-emblem-dark.png";

/**
 * Homepage — intentionally short and focused. Sections in order:
 *   1. Hero              — "Where Abu Dhabi bids on masterpieces"
 *   2. Watch of the Week — featured lot (renders nothing if none flagged)
 *   3. The Mazad way     — 4-step how-it-works
 *   4. Beyond the Sale   — the five concierge services
 *
 * The Market Pricing teaser has been removed from the public homepage;
 * pricing intelligence now lives inside the admin panel only.
 */
export default function Landing() {
  const { t } = useLang();
  const location = useLocation();

  // Smooth-scroll to hash targets when arriving at /#some-id.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace(/^#/, "");
    const el = document.getElementById(id);
    if (!el) return;
    const timer = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    return () => clearTimeout(timer);
  }, [location.hash, location.key]);

  return (
    <div className="min-h-screen bg-midnight-deep text-white">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/13398522/pexels-photo-13398522.jpeg)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-midnight-deep" />
        <img
          src={logoEmblem}
          alt=""
          aria-hidden="true"
          data-testid="hero-logo-watermark"
          className="pointer-events-none select-none absolute right-[-4%] top-1/2 -translate-y-1/2 w-[520px] max-w-[55%] opacity-[0.08] mix-blend-screen hidden md:block"
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-24">
          <div className="max-w-3xl">
            <div className="overline text-gold mb-6" data-testid="hero-kicker">{t("home.hero_kicker")}</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight" data-testid="hero-title">
              <span className="block">{t("home.hero_title")}</span>
            </h1>
            <p className="mt-8 text-lg text-white/70 max-w-xl leading-relaxed">{t("home.hero_sub")}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/catalog"
                data-testid="hero-shop-btn"
                className="inline-flex items-center bg-gold text-black px-8 py-4 uppercase tracking-widest text-xs font-medium hover:bg-gold-hover transition-all hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(212,175,55,0.35)]"
              >
                {t("home.cta_shop")} →
              </Link>
              <Link
                to="/auctions"
                data-testid="hero-auction-btn"
                className="inline-flex items-center border border-white/25 px-8 py-4 uppercase tracking-widest text-xs font-medium hover:bg-white/10 transition-all"
              >
                {t("home.cta_auction")}
              </Link>
              <Link
                to="/find-my-watch"
                data-testid="hero-find-btn"
                className="inline-flex items-center gap-2 border border-gold/60 text-gold px-8 py-4 uppercase tracking-widest text-xs font-medium hover:bg-gold/10 transition-all"
                title="AI concierge — describe what you're hunting for"
              >
                ✦ Find My Watch
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Watch of the Week — sits directly under the main hero. Renders
          nothing when no lot is flagged so the layout falls through cleanly. */}
      <WatchOfTheWeek />

      {/* The Mazad way */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="overline text-gold mb-3 text-center">{t("home.how_it_works")}</div>
        <h2 className="font-serif text-3xl md:text-5xl text-center mb-10">The Mazad way</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { n: "01", label: t("home.step1"), href: null },
            { n: "02", label: t("home.step2"), href: "/auctions", cta: "Place a pre-bid" },
            { n: "03", label: t("home.step3"), href: null },
            { n: "04", label: t("home.step4"), href: null },
          ].map((s) => (
            <div key={s.n} className="glass rounded-lg p-8 flex flex-col">
              <div className="font-serif text-6xl gold-gradient mb-4">{s.n}</div>
              <p className="text-white/80 leading-relaxed flex-1">{s.label}</p>
              {s.href && (
                <Link
                  to={s.href}
                  data-testid={`home-step-${s.n}-cta`}
                  className="mt-5 self-start inline-flex items-center gap-2 text-[11px] uppercase tracking-widest bg-gold/15 text-gold border border-gold/40 hover:bg-gold hover:text-black px-3 py-1.5 rounded-full transition-colors"
                >
                  {s.cta} →
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Beyond the Sale */}
      <ServicesSection />

      {/* Evaluate Your Watch — flat-fee written appraisal CTA. Placed
          after the concierge services so it reads as a natural next
          question ("what's mine worth?") for anyone who scrolled this
          far. Uses a warm-gold border rather than a full gold panel
          so it doesn't compete with the primary auction hero. */}
      <section
        className="max-w-7xl mx-auto px-6 lg:px-10 py-14"
        data-testid="home-evaluate-section"
      >
        <div className="glass-strong rounded-lg border border-gold/25 p-8 md:p-12 grid md:grid-cols-[1.3fr_1fr] gap-8 items-center overflow-hidden relative">
          <div className="pointer-events-none absolute -top-16 -end-16 w-64 h-64 rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
          <div className="relative">
            <div className="overline text-gold mb-3">Concierge Service · AED 100</div>
            <h2 className="font-serif text-3xl md:text-4xl mb-3">Curious what your watch is worth?</h2>
            <p className="text-white/70 leading-relaxed max-w-lg mb-6">
              Our specialists deliver a written evaluation — an honest condition assessment
              plus a fair market value range — for a flat AED 100. Typically returned within
              three business days, straight to your inbox and your account.
            </p>
            <Link
              to="/evaluate"
              data-testid="home-evaluate-cta"
              className="inline-flex items-center bg-gold text-black px-6 py-3 uppercase tracking-widest text-xs font-medium hover:bg-gold-hover transition-all hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(212,175,55,0.35)]"
            >
              Evaluate Your Watch →
            </Link>
          </div>
          <div className="relative hidden md:grid grid-cols-2 gap-3 text-sm">
            <MiniStat kicker="Value range" body="AED low – high, market-calibrated" />
            <MiniStat kicker="Condition" body="Case, dial, movement, provenance" />
            <MiniStat kicker="Turnaround" body="≤ 3 business days" />
            <MiniStat kicker="Deliverable" body="Email + shareable PDF" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function MiniStat({ kicker, body }) {
  return (
    <div className="rounded p-3 border border-white/5 bg-white/[0.02]">
      <div className="text-[10px] uppercase tracking-widest text-gold mb-1">{kicker}</div>
      <div className="text-white/80 text-[13px] leading-snug">{body}</div>
    </div>
  );
}
